import React, { useEffect, useRef, useState } from 'react';
import Button from '../../../ui/Button/Button';
import Dialog from '../../../ui/Dialog/Dialog';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import { confirmDialog } from '../../../ui/feedback';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ProgressBar from '../../../ui/Progress/ProgressBar';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import { CellText, CellPlate, CellMoney } from '../../../ui/DataTable/cells';
import Card from '../../../ui/Card';
import { Input } from '../../../ui/Field/Field';
import { finesService, fmtAED, errorMessage } from '../../../services/finesService';
import { followJob } from '../../../services/followJob';
import { Pill } from './ui';
import { useRowLinks, stickyTop } from './jobLinks';
import { dubaiIsoDay } from '../../../utils/dubaiTime';

// Remove the parking the engine charged twice (owner, 6 Oct 2026: an upload doubled parking staff had typed under
// another wording). The admin's alone. A day's engine parking lines on Ejar that double an older staff line of the
// same car and money, on a car Ejar holds more parking for than the portal shows; removed the way Ejar's own "Manage
// Other Charges" does it, under the admin's own Ejar login, read back from Ejar afterwards.

const TONE = { removed: 'green', already: 'indigo', failed: 'red', queued: 'gray' };
const LABEL = { removed: 'Removed', already: 'Already gone', failed: 'Failed', queued: 'Waiting' };
// the Dubai day (not UTC: after midnight Dubai toISOString() still says yesterday; not the browser's zone either)
const today = () => dubaiIsoDay();

const JobView = ({ job, onCancel, onBack, ContractLink }) => {
    const rows = Object.values(job.results || {});
    const s = job.summary || {};
    const running = job.status === 'running';
    const state = running ? 'running' : job.status === 'done' ? 'done' : 'stopped';
    return (
        <div>
            <div className={`${stickyTop} mb-1`}>
            <div className="mb-3">
                <ProgressBar done={job.done} total={job.total} unit="lines" state={state}
                             label={`Removing on Ejar as ${job.ejarUser}`} note={`${job.stage || ''}${job.error ? ` · ${job.error}` : ''}`} />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3 text-sm">
                <div className="flex flex-wrap gap-2">
                    {!running && ['removed', 'already', 'failed'].filter((k) => s[k]).map((k) => <Pill key={k} tone={TONE[k]}>{LABEL[k]}: {s[k]}</Pill>)}
                </div>
                <div className="flex gap-2">
                    {running && <Button size="sm" variant="danger" leadingIcon={<Icon glyph={ICONS.close} size="sm" />} onClick={onCancel}>Stop</Button>}
                    {!running && <Button size="sm" leadingIcon={<Icon glyph={ICONS.arrowLeft} size="sm" />} onClick={onBack}>Back to the list</Button>}
                </div>
            </div>
            </div>
            <TableContainer maxHeight={520}>
                <Table sticky>
                    <TableHead><TableRow><TableHeader>#</TableHeader><TableHeader>Contract</TableHeader><TableHeader>Car</TableHeader><TableHeader>Engine line</TableHeader><TableHeader>Amount</TableHeader><TableHeader>Result</TableHeader><TableHeader>Detail</TableHeader></TableRow></TableHead>
                    <TableBody>
                        {rows.map((r, i) => (
                            <TableRow key={r.lineId}>
                                <TableCell><span className="ds-cell-num">{i + 1}</span></TableCell>
                                <TableCell><ContractLink contract={r.contract} customer={r.customer} plate={r.car} /></TableCell><TableCell><CellPlate>{r.car}</CellPlate></TableCell>
                                <TableCell><CellText>{r.comment}</CellText></TableCell>
                                <TableCell><CellMoney value={Number(r.gross || 0)} currency="AED" /></TableCell>
                                <TableCell><Pill tone={TONE[r.status] || 'gray'}>{LABEL[r.status] || r.status}</Pill></TableCell>
                                <TableCell><CellText>{r.detail}</CellText></TableCell>
                            </TableRow>))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default function DoubledParkingRemoveModal({ onClose, onDone }) {
    const [day, setDay] = useState(today());
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const [sel, setSel] = useState(new Set());
    const [job, setJob] = useState(null);
    const [busy, setBusy] = useState(false);
    const changed = useRef(false);
    const { ContractLink, modals } = useRowLinks();

    const [reading, setReading] = useState(null);   // the list job while Ejar is read: a bar, x of y contracts
    const load = async (d) => {
        setError(null); setPreview(null); setReading(null);
        try {
            // the list is a background job (two Ejar reads a contract, a few hundred contracts a day): followed with a bar
            const started = await finesService.doubledParking({ commit: false, day: d });
            const done = await followJob(() => finesService.doubledParkingJob(started.jobId), {
                onUpdate: (j) => { setError(null); setReading(j); },
                onRetry: (e) => setError(`Lost contact with the engine (${errorMessage(e)}) - still following the read, retrying…`),
            });
            const p = done || reading;
            if (!p || p.status !== 'done') { setError(p?.error || 'the read stopped before the end'); setReading(null); return; }
            setPreview({ ...p, total: p.amount });
            setSel(new Set((p.rows || []).map((r) => r.lineId)));
        } catch (e) { setError(errorMessage(e)); }
        setReading(null);
    };
    const stopReading = async () => { if (reading?.jobId) { try { await finesService.doubledParkingStop(reading.jobId); } catch (e) { setError(errorMessage(e)); } } };
    useEffect(() => { load(day); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const rows = preview?.rows || [];
    const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const allChecked = rows.length > 0 && sel.size === rows.length;
    const toggleAll = () => setSel(allChecked ? new Set() : new Set(rows.map((r) => r.lineId)));
    const total = rows.filter((r) => sel.has(r.lineId)).reduce((s, r) => s + (r.gross || 0), 0);

    const start = async () => {
        const chosen = [...sel];
        if (!chosen.length) return;
        if (!(await confirmDialog({
            title: 'Remove the doubled parking on Ejar?',
            message: `Remove ${chosen.length} engine parking line(s) (${fmtAED(total)}, with VAT) from their contracts on Ejar as ${preview.ejarUser}? The older line of each stays.`,
            confirmLabel: 'Remove',
            danger: true,
        }))) return;
        setBusy(true); setError(null);
        try {
            const started = await finesService.doubledParking({ commit: true, day: preview.day, lines: chosen, listJobId: preview.jobId });
            changed.current = true;
            await followJob(() => finesService.doubledParkingJob(started.jobId), {
                onUpdate: (j) => { setError(null); setJob(j); },
                onRetry: (e) => setError(`Lost contact with the engine (${errorMessage(e)}) - still following the job, retrying…`),
            });
        } catch (e) { setError(errorMessage(e)); }
        setBusy(false);
    };
    const cancel = async () => { if (job?.jobId) { try { await finesService.doubledParkingStop(job.jobId); } catch (e) { setError(errorMessage(e)); } } };
    const close = () => { if (busy) return; if (changed.current) onDone?.(); onClose(); };

    return (
        <Dialog open onClose={close} size="xl" title="Parking charged twice on Ejar" closeOnOverlay={!busy}>
            {error && <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            {!preview && !error && !job && !reading && <LoadingSpinner message="Starting the read…" />}
            {reading && !preview && !job && (
                <div className="mb-3 flex flex-col gap-2">
                    <ProgressBar done={reading.done} total={reading.total} unit="contracts" state="running"
                                 label="Reading the day's contracts on Ejar" note={reading.stage || ''} />
                    <div><Button size="sm" variant="danger" leadingIcon={<Icon glyph={ICONS.close} size="sm" />} onClick={stopReading}>Stop</Button></div>
                </div>
            )}
            {!job && (
                <div className="flex flex-wrap items-end gap-3 mb-3 text-sm">
                    <label className="flex flex-col gap-1 text-gray-700">Upload day
                        <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} disabled={busy} />
                    </label>
                    <Button size="sm" onClick={() => load(day)} disabled={busy || !day || !!reading} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Read Ejar</Button>
                    {preview && (preview.mustConfigure
                        ? <span className="text-red-700 font-semibold">Save your own Ejar login first (The Engine → Ejar account).</span>
                        : <span className="text-gray-600">Lines are removed on Ejar as <b>{preview.ejarUser}</b>. Nothing is sent until you confirm.</span>)}
                </div>
            )}
            {preview && !job && (
                <>
                    <Card
                        className="mb-4"
                        title="Engine parking lines that double an older line"
                        subtitle="A car Ejar holds more parking for than the portal shows; each engine line of the day beside the older line of the same car and money it doubles (a staff line, or an earlier engine line). The older line stays."
                        actions={(
                            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                                <span>{sel.size} / {rows.length} selected · {fmtAED(total)} · {preview.contracts || 0} contract(s)</span>
                                <Button variant="danger" size="sm" disabled={preview.mustConfigure || busy || sel.size === 0} onClick={start}
                                        leadingIcon={<Icon glyph={ICONS.close} size="sm" />}>
                                    Remove on Ejar
                                </Button>
                            </div>
                        )}
                    >
                        {rows.length === 0 ? <div className="text-sm text-gray-500">Nothing doubled on this day: every engine parking line stands for a parking Ejar did not hold already.</div> : (
                            <TableContainer maxHeight={380}>
                                <Table sticky>
                                    <TableHead><TableRow>
                                        <TableHeader className="w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label={allChecked ? 'Clear selection' : 'Select all lines'} /></TableHeader>
                                        <TableHeader>#</TableHeader><TableHeader>Contract</TableHeader><TableHeader>Customer</TableHeader><TableHeader>Car</TableHeader>
                                        <TableHeader>Engine line (to remove)</TableHeader><TableHeader>Amount</TableHeader>
                                        <TableHeader>Older line (stays)</TableHeader><TableHeader>Typed by</TableHeader><TableHeader>Ejar / portal</TableHeader>
                                    </TableRow></TableHead>
                                    <TableBody>{rows.map((r, i) => (
                                        <TableRow key={r.lineId}>
                                            <TableCell><input type="checkbox" checked={sel.has(r.lineId)} onChange={() => toggle(r.lineId)} aria-label={`Select line ${r.lineId}`} /></TableCell>
                                            <TableCell><span className="ds-cell-num">{i + 1}</span></TableCell>
                                            <TableCell><ContractLink contract={r.contract} customer={r.customer} plate={r.car} /></TableCell><TableCell><CellText>{r.customer}</CellText></TableCell>
                                            <TableCell><CellPlate>{r.car}</CellPlate></TableCell>
                                            <TableCell><CellText>{r.comment}</CellText></TableCell>
                                            <TableCell><CellMoney value={Number(r.gross || 0)} currency="AED" /></TableCell>
                                            <TableCell><CellText>{r.olderComment}</CellText></TableCell>
                                            <TableCell><CellText>{r.olderBy}{r.olderAt ? ` · ${r.olderAt}` : ''}</CellText></TableCell>
                                            <TableCell><span className="ds-cell-num">{fmtAED(r.ejar)} / {fmtAED(r.portal)}</span></TableCell>
                                        </TableRow>))}</TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Card>
                    {preview.errors?.length > 0 && (
                        <details className="text-sm mt-1"><summary className="cursor-pointer text-gray-600">{preview.errors.length} contract(s) not read</summary>
                            <ul className="mt-1 ml-4 list-disc text-gray-600">{preview.errors.map((x) => <li key={x.contract}><span className="font-mono">{x.contract}</span>: {x.error}</li>)}</ul></details>
                    )}
                </>
            )}
            {job && <JobView job={job} onCancel={cancel} onBack={() => { setJob(null); load(day); }} ContractLink={ContractLink} />}
            {modals}
        </Dialog>
    );
}
